import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useErrorHandler } from "../hooks/useErrorHandler";
import {
  Add as AddIcon,
  EventNote,
  Person,
  CalendarToday,
  Description,
  CheckCircle,
  Cancel,
  Schedule,
  Close,
  Edit,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import api from "../services/api";
import { leaveService } from "../services";
import { useAuth } from "../context/AuthContext";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";

const validationSchema = Yup.object({
  leaveType: Yup.string()
    .oneOf(["sick", "casual", "vacation", "unpaid"], "Invalid leave type")
    .required("Leave type is required"),
  startDate: Yup.date()
    .min(new Date(), "Start date cannot be in the past")
    .required("Start date is required"),
  endDate: Yup.date()
    .min(Yup.ref("startDate"), "End date must be after start date")
    .required("End date is required"),
  reason: Yup.string()
    .min(10, "Reason must be at least 10 characters")
    .required("Reason is required"),
});

const getStatusIcon = (status) => {
  switch (status) {
    case "approved":
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    case "rejected":
      return <Cancel className="w-5 h-5 text-red-500" />;
    case "pending":
      return <Schedule className="w-5 h-5 text-yellow-500" />;
    default:
      return <Schedule className="w-5 h-5 text-gray-500" />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "approved":
      return "bg-green-100 text-green-700 border-green-200";
    case "rejected":
      return "bg-red-100 text-red-700 border-red-200";
    case "pending":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

export default function Leave() {
  const { user } = useAuth();
  const { showError, reset: resetError } = useErrorHandler();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [leaveToReject, setLeaveToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [processing, setProcessing] = useState(false);
  // Check if user is EMPLOYEE (handle both old and new role formats)
  const userRole = user?.role?.toUpperCase() || user?.role;
  const isEmployee = userRole === "EMPLOYEE" || user?.role === "employee";
  const isAdminOrManager = userRole === "ADMIN" || userRole === "MANAGER";

  // Get status from URL query parameter, default based on role
  const statusFromUrl = searchParams.get("status");
  const initialStatus = statusFromUrl || (isEmployee ? "all" : "pending");

  const [filters, setFilters] = useState({
    status: initialStatus,
    employeeId: "",
    startDate: "",
    endDate: "",
  });

  // Update filters when URL query parameter changes
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      // Update filter to match URL parameter
      setFilters((prev) => {
        if (prev.status !== statusParam) {
          console.log(
            `🔄 Updating filter from URL: ${prev.status} → ${statusParam}`
          );
          return {
            ...prev,
            status: statusParam,
          };
        }
        return prev;
      });
    }
  }, [searchParams]);

  const formik = useFormik({
    initialValues: {
      leaveType: "casual",
      startDate: "",
      endDate: "",
      reason: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = {
          leave_type: values.leaveType,
          from_date: values.startDate,
          to_date: values.endDate,
          reason: values.reason,
        };

        if (editingLeave) {
          const leaveId = editingLeave.id || editingLeave._id;
          if (!leaveId) {
            toast.error("Invalid leave request");
            setSubmitting(false);
            return;
          }
          await api.put(`/leave/${leaveId}`, payload);
          toast.success("Leave request updated successfully!");
        } else {
          await api.post("/leave/request", payload);
          toast.success("Leave request submitted successfully!");
        }

        resetForm();
        setOpen(false);
        setEditingLeave(null);
        fetchLeaves();
      } catch (err) {
        const message =
          err.response?.data?.error ||
          err.response?.data?.message ||
          (editingLeave
            ? "Failed to update leave request"
            : "Failed to submit leave request");
        toast.error(message);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fetchLeaves = useCallback(async () => {
    if (!user) return;

    resetError(); // Reset error handler for new fetch attempt

    try {
      setLoading(true);
      // Employees should only see their own requests
      // Admin/Manager can see pending requests for approval
      const endpoint = isEmployee ? "/leave/my-requests" : "/leave/pending";
      const params = {
        status: filters.status,
      };

      console.log(`📋 Fetching leaves with filter:`, {
        endpoint,
        status: filters.status,
        isEmployee,
      });

      if (filters.startDate) {
        params.startDate = filters.startDate;
      }

      if (filters.endDate) {
        params.endDate = filters.endDate;
      }

      if (!isEmployee && filters.employeeId) {
        params.employeeId = filters.employeeId;
      }

      const response = await api.get(endpoint, { params });
      const payload = response.data;
      const leavesData = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      setLeaves(leavesData);
    } catch (err) {
      console.error("Failed to fetch leaves", err);
      console.error("Error details:", {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        url: err.config?.url,
        hasToken: !!localStorage.getItem("token"),
      });

      // Provide more specific error messages
      if (err.response?.status === 401) {
        showError(err, "Authentication failed. Please log in again.");
      } else if (err.response?.status === 404) {
        const errorMessage =
          err.response?.data?.message || err.response?.data?.error;
        if (errorMessage?.includes("Employee record not found")) {
          showError(
            err,
            "Employee profile not found. Please contact HR to set up your employee record."
          );
        } else {
          showError(err, "Leave records not found.");
        }
      } else if (err.response?.status === 403) {
        showError(err, "You don't have permission to view leave records.");
      } else {
        showError(err, "Failed to load leave records");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user,
    filters.status,
    filters.employeeId,
    filters.startDate,
    filters.endDate,
  ]);

  // Don't reset filters on role change if URL has a status parameter
  // This allows dashboard clicks to work properly
  useEffect(() => {
    if (user && !searchParams.get("status")) {
      const userRole = user?.role?.toUpperCase() || user?.role;
      const isEmp = userRole === "EMPLOYEE" || user?.role === "employee";
      setFilters((prev) => ({
        ...prev,
        status: isEmp ? "all" : "pending",
        employeeId: "",
        startDate: "",
        endDate: "",
      }));
    }
  }, [user?.role, searchParams]);

  useEffect(() => {
    if (user) {
      fetchLeaves();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user,
    filters.status,
    filters.employeeId,
    filters.startDate,
    filters.endDate,
  ]);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const response = await api.get("/employees");
        const payload = response.data;
        const employeeData = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];
        setEmployees(employeeData);
      } catch (err) {
        console.error("Failed to load employees list", err);
      }
    };

    if (user && !isEmployee) {
      loadEmployees();
    }
  }, [user]);

  const handleFilterChange = (field) => (event) => {
    const { value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Update URL query parameter when status filter changes
    if (field === "status") {
      const newSearchParams = new URLSearchParams(searchParams);
      if (value && value !== "all") {
        newSearchParams.set("status", value);
      } else {
        newSearchParams.delete("status");
      }
      setSearchParams(newSearchParams, { replace: true });
    }
  };

  const handleResetFilters = () => {
    if (!user) return;
    setFilters({
      status: isEmployee ? "all" : "pending",
      employeeId: "",
      startDate: "",
      endDate: "",
    });
  };

  const handleApprove = async (leaveId) => {
    try {
      setProcessing(true);
      await leaveService.approve(leaveId);
      toast.success("Leave request approved successfully!");
      fetchLeaves(); // Refresh the list
    } catch (err) {
      console.error("Failed to approve leave:", err);
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to approve leave request"
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectClick = (leave) => {
    setLeaveToReject(leave);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!leaveToReject) return;

    try {
      setProcessing(true);
      await leaveService.reject(
        leaveToReject._id || leaveToReject.id,
        rejectionReason
      );
      toast.success("Leave request rejected successfully!");
      setRejectDialogOpen(false);
      setLeaveToReject(null);
      setRejectionReason("");
      fetchLeaves(); // Refresh the list
    } catch (err) {
      console.error("Failed to reject leave:", err);
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to reject leave request"
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setLeaveToReject(null);
    setRejectionReason("");
  };
  const statusOptions = [
    { label: "All Statuses", value: "all" },
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8 flex justify-between items-center flex-wrap gap-4"
      >
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2">
            Leave Management
          </h1>
          <p className="text-gray-600">
            {isEmployee
              ? "View and request your leaves"
              : "Manage employee leave requests"}
          </p>
        </div>
        {isEmployee && (
          <motion.button
            onClick={() => setOpen(true)}
            className="btn-primary flex items-center gap-2 px-6 py-3"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <AddIcon />
            <span>Request Leave</span>
          </motion.button>
        )}
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card mb-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={handleFilterChange("status")}
              className="input-field"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {!isEmployee && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Employee
              </label>
              <select
                value={filters.employeeId}
                onChange={handleFilterChange("employeeId")}
                className="input-field"
              >
                <option value="">All Employees</option>
                {employees.map((employee) => (
                  <option key={employee._id} value={employee._id}>
                    {employee.user_id?.name || "Unknown"}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Input
            type="date"
            label="Start Date"
            value={filters.startDate}
            onChange={handleFilterChange("startDate")}
          />
          <Input
            type="date"
            label="End Date"
            value={filters.endDate}
            onChange={handleFilterChange("endDate")}
          />
        </div>
        <div className="mt-4 flex justify-end">
          <Button variant="secondary" onClick={handleResetFilters}>
            Clear Filters
          </Button>
        </div>
      </motion.div>

      {/* Leaves Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="w-16 h-16 skeleton rounded-2xl"></div>
                <div className="w-8 h-8 skeleton rounded-xl"></div>
              </div>
              <div className="h-6 skeleton rounded mb-2 w-3/4"></div>
              <div className="h-4 skeleton rounded w-full mb-2"></div>
              <div className="h-4 skeleton rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : leaves.length === 0 ? (
        <div className="card text-center py-12">
          <EventNote className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No leave requests found</p>
        </div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {leaves.map((leave, index) => (
            <motion.div
              key={leave._id || leave.id}
              variants={cardVariants}
              className="card group cursor-pointer"
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center text-white shadow-lg">
                    <EventNote className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-1 capitalize">
                      {leave.leave_type}
                    </h3>
                    {!isEmployee && (
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Person className="w-4 h-4" />
                        {leave.employee_id?.user_id?.name || "N/A"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isEmployee && leave.status === "pending" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingLeave(leave);
                        formik.setValues({
                          leaveType: leave.leave_type,
                          startDate: leave.from_date
                            ? new Date(leave.from_date)
                                .toISOString()
                                .split("T")[0]
                            : "",
                          endDate: leave.to_date
                            ? new Date(leave.to_date)
                                .toISOString()
                                .split("T")[0]
                            : "",
                          reason: leave.reason || "",
                        });
                        setOpen(true);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit leave request"
                    >
                      <Edit className="w-4 h-4 text-gray-600" />
                    </button>
                  )}
                  <div
                    className={`px-3 py-1 rounded-full text-xs font-semibold border flex items-center gap-1 ${getStatusColor(
                      leave.status
                    )}`}
                  >
                    {getStatusIcon(leave.status)}
                    <span className="capitalize">{leave.status}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3 text-sm">
                  <CalendarToday className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    {leave.from_date
                      ? new Date(leave.from_date).toLocaleDateString()
                      : "--"}{" "}
                    -{" "}
                    {leave.to_date
                      ? new Date(leave.to_date).toLocaleDateString()
                      : "--"}
                  </span>
                </div>
                {leave.reason && (
                  <div className="flex items-start gap-3 text-sm">
                    <Description className="w-4 h-4 text-gray-400 mt-0.5" />
                    <p className="text-gray-600 line-clamp-2">{leave.reason}</p>
                  </div>
                )}
              </div>

              {/* Approve/Reject Actions for Admin/Manager */}
              {isAdminOrManager && leave.status === "pending" && (
                <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApprove(leave._id || leave.id);
                    }}
                    disabled={processing}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRejectClick(leave);
                    }}
                    disabled={processing}
                    variant="outline"
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <Cancel className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Rejection Reason Dialog */}
      <AnimatePresence>
        {rejectDialogOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleRejectCancel}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />
            {/* Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-3xl shadow-soft-lg max-w-md w-full">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <h2 className="text-2xl font-bold text-primary">
                    Reject Leave Request
                  </h2>
                  <button
                    onClick={handleRejectCancel}
                    className="p-2 hover:bg-gray-100 rounded-2xl transition-colors"
                  >
                    <Close className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="mb-4">
                    <p className="text-gray-700 mb-2">
                      <strong>Employee:</strong>{" "}
                      {leaveToReject?.employee_id?.user_id?.name || "N/A"}
                    </p>
                    <p className="text-gray-700 mb-2">
                      <strong>Leave Type:</strong>{" "}
                      {leaveToReject?.leave_type?.toUpperCase() || "N/A"}
                    </p>
                    <p className="text-gray-700">
                      <strong>Dates:</strong>{" "}
                      {leaveToReject?.from_date
                        ? new Date(leaveToReject.from_date).toLocaleDateString()
                        : "--"}{" "}
                      -{" "}
                      {leaveToReject?.to_date
                        ? new Date(leaveToReject.to_date).toLocaleDateString()
                        : "--"}
                    </p>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Rejection Reason <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Please provide a reason for rejection..."
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                      required
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button
                      onClick={handleRejectCancel}
                      variant="outline"
                      className="flex-1"
                      disabled={processing}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleRejectConfirm}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                      disabled={processing || !rejectionReason.trim()}
                    >
                      {processing ? "Rejecting..." : "Reject Leave"}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Request Leave Modal */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setOpen(false);
                formik.resetForm();
                setEditingLeave(null);
              }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-white rounded-3xl shadow-soft-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <h2 className="text-2xl font-bold text-primary">
                    {editingLeave ? "Edit Leave Request" : "Request Leave"}
                  </h2>
                  <button
                    onClick={() => {
                      setOpen(false);
                      formik.resetForm();
                      setEditingLeave(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-2xl transition-colors"
                  >
                    <Close className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={formik.handleSubmit} className="p-6">
                  <div className="space-y-4">
                    {/* Leave Type */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Leave Type
                      </label>
                      <select
                        id="leaveType"
                        name="leaveType"
                        value={formik.values.leaveType}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`input-field ${
                          formik.touched.leaveType && formik.errors.leaveType
                            ? "border-red-500 focus:ring-red-500/20"
                            : ""
                        }`}
                      >
                        <option value="sick">Sick Leave</option>
                        <option value="casual">Casual Leave</option>
                        <option value="vacation">Vacation</option>
                        <option value="unpaid">Unpaid Leave</option>
                      </select>
                      {formik.touched.leaveType && formik.errors.leaveType && (
                        <p className="mt-2 text-sm text-red-600">
                          {formik.errors.leaveType}
                        </p>
                      )}
                    </div>

                    {/* Date Range */}
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        type="date"
                        label="Start Date"
                        id="startDate"
                        name="startDate"
                        value={formik.values.startDate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.startDate && formik.errors.startDate
                            ? formik.errors.startDate
                            : undefined
                        }
                      />
                      <Input
                        type="date"
                        label="End Date"
                        id="endDate"
                        name="endDate"
                        value={formik.values.endDate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.endDate && formik.errors.endDate
                            ? formik.errors.endDate
                            : undefined
                        }
                      />
                    </div>

                    {/* Reason */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Reason
                      </label>
                      <textarea
                        id="reason"
                        name="reason"
                        rows={4}
                        value={formik.values.reason}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`input-field resize-none ${
                          formik.touched.reason && formik.errors.reason
                            ? "border-red-500 focus:ring-red-500/20"
                            : ""
                        }`}
                        placeholder="Please provide a reason for your leave request..."
                      />
                      {formik.touched.reason && formik.errors.reason && (
                        <p className="mt-2 text-sm text-red-600">
                          {formik.errors.reason}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setOpen(false);
                        formik.resetForm();
                        setEditingLeave(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      loading={formik.isSubmitting}
                      disabled={formik.isSubmitting}
                    >
                      {formik.isSubmitting
                        ? editingLeave
                          ? "Updating..."
                          : "Submitting..."
                        : editingLeave
                        ? "Update Request"
                        : "Submit Request"}
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
